import { combineSlices } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage/session";
// uncomment to use local storage
// import storage from "redux-persist/lib/storage"
import { localizationSlice } from "../modules/localization";
import { resourcesApi } from "../modules/resources";
import { promptFormSlice } from "../modules/prompts/slices/promptFormSlice";
import { promptHistorySlice } from "../modules/prompts/slices/promptHistorySlice";
import { authSlice } from "../modules/auth";
import { providerKeysApi, usersApi } from "../modules/users";
import { promptRunnerApi, promptApi } from "../modules/prompts/api";
import { paymentsApi } from "../modules/payments/api/paymentsAPI";
import { preferencesSlice } from "../modules/preferences";
import { modelsApi } from "../modules/models/api/modelsApi";
import { featuresApi } from "../modules/features";
import { filesApi } from "../modules/files";
import { chatsApi } from "../modules/chats/api";
import { evalsApi, evalRunnerApi } from "../modules/evals/api";
import { evalResultsToolbarSlice } from "../modules/evals/slices/evalResultsToolbarSlice";
import { evalLockedColumnsSlice } from "../modules/evals/slices/evalLockedColumnsSlice";
import { judgementsApi } from "../modules/judgements/api/judgementsApi";
import { upsellSlice } from "../modules/payments/slices/upsellSlice";
import { systemMessageSlice } from "../modules/system-message";

/**
 * State version number - increment this when making breaking changes
 * to the persisted state structure or backend data models.
 *
 * When to increment:
 * - Backend API response structure changes
 * - Slice state shape modifications
 * - Data model migrations that make old persisted data incompatible
 *
 * Incrementing this version will automatically purge all persisted state
 * for users, forcing a fresh start with the new data structure.
 */
const STATE_VERSION = 3;

const persistConfig = {
  key: "PersistedState",
  storage,
  whitelist: [preferencesSlice.reducerPath],
  migrate: async <T>(state: T) => {
    const persistedVersion = await storage.getItem("PersistedState:version");
    const currentVersion = STATE_VERSION.toString();

    if (persistedVersion !== currentVersion) {
      console.info(
        `State version outdated (persisted: ${persistedVersion}, current: ${STATE_VERSION}). Purging persisted state.`,
      );

      await storage.setItem("PersistedState:version", currentVersion);

      return Promise.resolve(undefined);
    }
    // If versions match, return the state as-is
    return Promise.resolve(state);
  },
};

function createAppReducer() {
  const reducers = combineSlices(
    // api
    resourcesApi,
    promptApi,
    usersApi,
    providerKeysApi,
    promptRunnerApi,
    modelsApi,
    paymentsApi,
    featuresApi,
    filesApi,
    chatsApi,
    evalsApi,
    evalRunnerApi,
    judgementsApi,
    //slices
    localizationSlice,
    authSlice,
    promptFormSlice,
    promptHistorySlice,
    preferencesSlice,
    upsellSlice,
    evalResultsToolbarSlice,
    evalLockedColumnsSlice,
    systemMessageSlice,
  );

  return persistReducer(persistConfig, reducers);
}

export { createAppReducer };
